import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Search,
  Plus,
  RotateCcw,
  Store,
  Eye,
  Loader2,
  X,
  Edit2,
  Save,
} from "lucide-react";
import { PaginationControls } from "@/components/common/PaginationControl";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { config } from "@/config";
import { SettingsApi } from "@/api/giftcard.api";

interface Merchant {
  id: number;
  createdDate: string;
  updatedDate: string;
  name: string;
  merchantCode: string;
  licenseNumber: string;
  email: string;
  phoneNumber: string;
  address: string;
  category: string;
  status: "Active" | "Inactive";
  _raw?: any; // Store raw API response for full details
}

const GiftCardBrands: React.FC = () => {
  const navigate = useNavigate();
  const [merchants, setMerchants] = useState<Merchant[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedMerchant, setSelectedMerchant] = useState<Merchant | null>(
    null
  );
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editFormData, setEditFormData] = useState<any>({});
  const [settingsCategories, setSettingsCategories] = useState<string[]>([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [malls, setMalls] = useState<Array<{ id: number; name: string }>>([]);
  const [selectedMallId, setSelectedMallId] = useState<number | null>(null);
  const [isMallLoading, setIsMallLoading] = useState(false);

  const fetchMalls = async () => {
    setIsMallLoading(true);
    try {
      const data = await SettingsApi.getMallDetails();
      const mallsData = Array.isArray(data?.malldetails)
        ? data.malldetails
        : [];
      const mapped = mallsData.map((mall: any) => ({
        id: mall.mallId,
        name: mall.mallName || `Mall ${mall.mallId}`,
      }));
      setMalls(mapped);
      // Set first mall as selected initially
      if (mapped.length > 0 && selectedMallId === null) {
        setSelectedMallId(mapped[0].id);
      }
    } catch (error) {
      console.error("Error fetching malls:", error);
      setMalls([]);
    } finally {
      setIsMallLoading(false);
    }
  };

  const fetchMerchants = async () => {
    setIsLoading(true);
    try {
      // Get userId from session storage
      const userInfo = sessionStorage.getItem("userInfo");
      let userId = "";
      if (userInfo) {
        try {
          const parsed = JSON.parse(userInfo);
          userId =
            (parsed && (parsed.userId ?? parsed.id ?? "")).toString() ?? "";
        } catch (error) {
          console.warn("Failed to parse userInfo from session storage", error);
        }
      }

      const response = await fetch(
        `${config.GIFT_API_BASE_URL}/Merchant/GetAllMerchant`,
        {
          method: "POST",
          headers: {
            accept: "text/plain",
            "Content-Type": "application/json",
            XApiKey: config.GIFT_API_KEY,
          },
          body: JSON.stringify({
            p_UserId: userId,
            p_PageNumber: 1,
            p_PageSize: rowsPerPage,
            p_MallId: selectedMallId ?? 1,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch merchants");
      }

      const data = await response.json();

      if (data.error_msg) {
        toast.error(data.error_msg || "Failed to fetch merchants");
        setMerchants([]);
        return;
      }

      // Map API response to merchant interface
      const mappedMerchants: Merchant[] = (data.merchants || []).map(
        (merchant: any) => ({
          id: merchant.id,
          createdDate: merchant.createdDatetime || "",
          updatedDate: merchant.updatedDatetime || "",
          name: merchant.businessName || "N/A",
          merchantCode: merchant.merchantCode || "N/A",
          licenseNumber: merchant.traderlicenseNumber || "N/A",
          email: merchant.email || "N/A",
          phoneNumber: merchant.phoneNo || "N/A",
          address: merchant.address || "N/A",
          category: merchant.category || "N/A",
          status: merchant.isActive ? "Active" : "Inactive",
          _raw: merchant, // Store raw API response
        })
      );

      setMerchants(mappedMerchants);
    } catch (error) {
      console.error("Error fetching merchants:", error);
      toast.error("Failed to fetch merchants");
      setMerchants([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCategories = useCallback(async () => {
    if (selectedMallId === null) {
      return;
    }
    setIsLoadingCategories(true);
    try {
      const userInfoRaw = sessionStorage.getItem("userInfo");
      const userId = userInfoRaw ? JSON.parse(userInfoRaw)?.userId ?? 0 : 0;

      const response = await fetch(
        `${config.GIFT_API_BASE_URL}/Settings/GetSettings`,
        {
          method: "POST",
          headers: {
            xapikey: config.GIFT_API_KEY,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            p_Id: userId,
            p_MallId: selectedMallId,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch settings");
      }

      const data = await response.json();
      const categoryList = data?.setting?.categoryList;

      if (categoryList) {
        const parsed = categoryList
          .split(",")
          .map((item: string) => item.trim())
          .filter((item: string) => item.length > 0);
        setSettingsCategories(parsed);
      } else {
        setSettingsCategories([]);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      setSettingsCategories([]);
      toast.error("Failed to load categories");
    } finally {
      setIsLoadingCategories(false);
    }
  }, [selectedMallId]);

  useEffect(() => {
    fetchMalls();
  }, []);

  useEffect(() => {
    if (selectedMallId !== null) {
      fetchCategories();
    }
  }, [selectedMallId, fetchCategories]);

  useEffect(() => {
    if (selectedMallId !== null) {
      fetchMerchants();
    }
  }, [selectedMallId, rowsPerPage]);

  // Filter merchants
  const filteredMerchants = merchants.filter((merchant) => {
    const matchesSearch = merchant.name
      .toLowerCase()
      .includes(searchTerm.toLowerCase());

    const matchesCategory =
      categoryFilter === "all" || merchant.category === categoryFilter;

    return matchesSearch && matchesCategory;
  });

  // Pagination
  const totalPages = Math.ceil(filteredMerchants.length / rowsPerPage);
  const currentPageData = filteredMerchants.slice(
    page * rowsPerPage,
    (page + 1) * rowsPerPage
  );

  // Reset filters
  const handleReset = () => {
    setSearchTerm("");
    setCategoryFilter("all");
    setPage(0);
  };

  // Get status badge color
  const getStatusColor = (status: string) => {
    switch (status) {
      case "Active":
        return "bg-green-100 text-green-800";
      case "Inactive":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Format date helper
  const formatDate = (dateString: string) => {
    if (!dateString || dateString === "N/A") return "N/A";
    try {
      return format(new Date(dateString), "MMM dd, yyyy");
    } catch {
      return "N/A";
    }
  };

  // Handle edit mode toggle
  const handleEditClick = () => {
    if (selectedMerchant && selectedMerchant._raw) {
      setEditFormData({
        id: selectedMerchant._raw.id,
        businessName: selectedMerchant._raw.businessName || "",
        traderlicenseNumber: selectedMerchant._raw.traderlicenseNumber || "",
        phoneNo: selectedMerchant._raw.phoneNo || "",
        email: selectedMerchant._raw.email || "",
        address: selectedMerchant._raw.address || "",
        category: selectedMerchant._raw.category || "",
        webSite: selectedMerchant._raw.webSite || "",
        isActive: selectedMerchant._raw.isActive,
      });
      setIsEditMode(true);
    }
  };

  // Handle form field changes
  const handleFieldChange = (field: string, value: any) => {
    setEditFormData((prev: any) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle phone number change with validation
  const handlePhoneNumberChange = (value: string) => {
    // Only allow numeric characters
    const sanitized = value.replace(/\D/g, "");
    // Limit to 15 digits
    const limited = sanitized.slice(0, 15);
    handleFieldChange("phoneNo", limited);
  };

  // Handle save merchant
  const handleSaveMerchant = async () => {
    // Phone number validation
    const phoneDigits = (editFormData.phoneNo || "").replace(/\D/g, "");
    if (phoneDigits.length < 7) {
      toast.error("Phone number must be at least 7 digits");
      return;
    }
    if (phoneDigits.length > 15) {
      toast.error("Phone number must not exceed 15 digits");
      return;
    }

    setIsSaving(true);
    try {
      // Get userId and username from session storage
      const userInfo = sessionStorage.getItem("userInfo");
      const parsedUserInfo = userInfo ? JSON.parse(userInfo) : null;
      const userId = parsedUserInfo?.userId || "";
      const userName = parsedUserInfo?.username || "";

      const response = await fetch(
        `${config.GIFT_API_BASE_URL}/Merchant/CreateUpdateMerchant`,
        {
          method: "POST",
          headers: {
            accept: "text/plain",
            "Content-Type": "application/json",
            XApiKey: config.GIFT_API_KEY,
          },
          body: JSON.stringify({
            id: editFormData.id,
            businessName: editFormData.businessName,
            traderlicenseNumber: editFormData.traderlicenseNumber,
            phoneNo: editFormData.phoneNo,
            email: editFormData.email,
            address: editFormData.address,
            category: editFormData.category,
            webSite: editFormData.webSite,
            isActive: editFormData.isActive,
            updatedBy: userId,
            userName: userName,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update merchant");
      }

      const data = await response.json();

      if (data.error_msg) {
        toast.error(data.error_msg || "Failed to update merchant");
        return;
      }

      toast.success("Brand updated successfully!");
      setIsEditMode(false);
      setIsDetailsModalOpen(false);

      // Refresh merchant list
      await fetchMerchants();
    } catch (error) {
      console.error("Error updating merchant:", error);
      toast.error("Failed to update merchant");
    } finally {
      setIsSaving(false);
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditFormData({});
  };

  // Table columns
  const columns: any[] = [
    {
      header: "Brand Name",
      accessorKey: "name",
      cell: (row: Merchant) => (
        <div className="font-medium text-gray-900">{row.name}</div>
      ),
    },
    {
      header: "Brand Code",
      accessorKey: "merchantCode",
    },
    {
      header: "Contact No",
      accessorKey: "phoneNumber",
    },
    {
      header: "Address",
      accessorKey: "address",
      cell: (row: Merchant) => (
        <span
          className="text-sm text-gray-600 line-clamp-1"
          title={row.address}
        >
          {row.address}
        </span>
      ),
    },
    {
      header: "Category",
      accessorKey: "category",
      cell: (row: Merchant) => (
        <span className="text-sm font-medium text-gray-700">
          {row.category}
        </span>
      ),
    },
    {
      header: "Status",
      accessorKey: "status",
      cell: (row: Merchant) => (
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
            row.status
          )}`}
        >
          {row.status}
        </span>
      ),
    },
    {
      header: "Action",
      id: "action",
      cell: (row: Merchant) => (
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setSelectedMerchant(row);
            setIsDetailsModalOpen(true);
          }}
          className="gap-2"
        >
          <Eye className="h-4 w-4" />
          View
        </Button>
      ),
    },
  ];

  // Categories for filters and editing
  // Use mall-specific categories from settings (categoryList) only.
  // If settingsCategories is empty for a mall, we intentionally show no category options
  // instead of falling back to derived categories from merchants.
  const availableCategories = settingsCategories;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Store className="h-8 w-8 text-amber-600" />
            Brands
          </h1>
          <p className="text-gray-600 mt-1">
            Manage and view all brands accepting gift cards
          </p>
        </div>
        <div className="flex items-center gap-4">
          {/* Mall Dropdown */}
          <div className="w-48">
            <Select
              value={
                selectedMallId !== null ? selectedMallId.toString() : undefined
              }
              onValueChange={(value) => setSelectedMallId(Number(value))}
              disabled={isMallLoading || malls.length === 0}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    isMallLoading ? "Loading malls..." : "Select mall"
                  }
                />
              </SelectTrigger>
              <SelectContent className="!max-h-48 overflow-auto py-0">
                {malls.length > 0 ? (
                  malls.map((mall) => (
                    <SelectItem key={mall.id} value={mall.id.toString()}>
                      {mall.name}
                    </SelectItem>
                  ))
                ) : (
                  <div className="px-3 py-2 text-sm text-gray-500">
                    {isMallLoading ? "Loading..." : "No malls found"}
                  </div>
                )}
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={() => navigate("/giftcard/brands/create")}
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Brand
          </Button>
        </div>
      </div>

      {/* Filter Section */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by Brand Name"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPage(0);
                  }}
                  className="pl-9"
                />
              </div>
            </div>

            {/* Category Filter */}
            <div className="w-full sm:w-48">
              <Select
                value={categoryFilter}
                onValueChange={(value) => {
                  setCategoryFilter(value);
                  setPage(0);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Filter by category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {availableCategories.map((category) => (
                    <SelectItem key={category} value={category}>
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Reset Button */}
            <Button variant="outline" onClick={handleReset} className="gap-2">
              <RotateCcw className="h-4 w-4" />
              Reset
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
              <span className="ml-2 text-gray-600">Loading merchants...</span>
            </div>
          ) : (
            <DataTable
              title="Brands"
              data={currentPageData}
              columns={columns}
            />
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {!isLoading && (
        <div className="mt-4 flex justify-between items-center">
          {/* Rows per page selector */}
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-700">Rows per page:</span>
            <Select
              value={rowsPerPage.toString()}
              onValueChange={(value) => {
                setRowsPerPage(parseInt(value));
                setPage(0); // Reset to first page when changing rows per page
              }}
            >
              <SelectTrigger className="w-20">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="5">5</SelectItem>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="30">30</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Pagination controls */}
          {totalPages > 1 && (
            <PaginationControls
              currentPage={page}
              totalPages={totalPages}
              onPrevHandler={() => setPage(Math.max(0, page - 1))}
              onNextHandler={() => setPage(Math.min(totalPages - 1, page + 1))}
              onPageChange={setPage}
            />
          )}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && filteredMerchants.length === 0 && (
        <div className="text-center py-12">
          <Store className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            No merchants found
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {merchants.length === 0
              ? "No merchants available. Add your first merchant."
              : "Try adjusting your search or filter criteria"}
          </p>
        </div>
      )}

      {/* Brand Details Modal */}
      <Dialog
        open={isDetailsModalOpen}
        onOpenChange={(open) => {
          setIsDetailsModalOpen(open);
          if (!open) {
            setIsEditMode(false);
            setEditFormData({});
          }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="text-2xl font-bold text-amber-700">
                  {isEditMode ? "Edit Brand" : "Brand Details"}
                </DialogTitle>
                <DialogDescription>
                  {isEditMode
                    ? "Update brand information"
                    : "Complete information about the brand"}
                </DialogDescription>
              </div>
              {!isEditMode && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleEditClick}
                  className="gap-2"
                >
                  <Edit2 className="h-4 w-4" />
                  Edit
                </Button>
              )}
            </div>
          </DialogHeader>

          {selectedMerchant && selectedMerchant._raw && (
            <div className="space-y-6 mt-4">
              {/* Status Badge */}
              <div className="flex items-center justify-between pb-4 border-b">
                <h3 className="text-lg font-semibold text-gray-900">
                  {isEditMode
                    ? editFormData.businessName
                    : selectedMerchant.name}
                </h3>
                {!isEditMode && (
                  <Badge
                    className={
                      selectedMerchant.status === "Active"
                        ? "bg-green-100 text-green-800 hover:bg-green-100"
                        : "bg-red-100 text-red-800 hover:bg-red-100"
                    }
                  >
                    {selectedMerchant.status}
                  </Badge>
                )}
                {isEditMode && (
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-bold text-gray-700">
                      Active:
                    </label>
                    <input
                      type="checkbox"
                      checked={editFormData.isActive}
                      onChange={(e) =>
                        handleFieldChange("isActive", e.target.checked)
                      }
                      className="h-5 w-5"
                    />
                  </div>
                )}
              </div>

              {/* Brand Information Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700">
                    Brand ID
                  </label>
                  <p className="text-base text-gray-900">
                    {selectedMerchant._raw.id || "N/A"}
                  </p>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700">
                    Business Name
                  </label>
                  {isEditMode ? (
                    <Input
                      value={editFormData.businessName}
                      onChange={(e) =>
                        handleFieldChange("businessName", e.target.value)
                      }
                      placeholder="Enter business name"
                    />
                  ) : (
                    <p className="text-base text-gray-900">
                      {selectedMerchant._raw.businessName || "N/A"}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700">
                    Trader License Number
                  </label>
                  {isEditMode ? (
                    <Input
                      value={editFormData.traderlicenseNumber}
                      onChange={(e) =>
                        handleFieldChange(
                          "traderlicenseNumber",
                          e.target.value.replace(/[^a-zA-Z0-9]/g, "")
                        )
                      }
                      placeholder="Enter license number"
                    />
                  ) : (
                    <p className="text-base text-gray-900">
                      {selectedMerchant._raw.traderlicenseNumber || "N/A"}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700">
                    Phone Number
                  </label>
                  {isEditMode ? (
                    <Input
                      type="tel"
                      inputMode="numeric"
                      value={editFormData.phoneNo}
                      onChange={(e) => handlePhoneNumberChange(e.target.value)}
                      placeholder="Enter phone number (7-15 digits)"
                    />
                  ) : (
                    <p className="text-base text-gray-900">
                      {selectedMerchant._raw.phoneNo || "N/A"}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700">
                    Email
                  </label>
                  {isEditMode ? (
                    <Input
                      value={editFormData.email}
                      onChange={(e) =>
                        handleFieldChange("email", e.target.value)
                      }
                      placeholder="Enter email"
                    />
                  ) : (
                    <p className="text-base text-gray-900 break-all">
                      {selectedMerchant._raw.email || "N/A"}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700">
                    Category
                  </label>
                  {isEditMode ? (
                    <Select
                      value={editFormData.category || ""}
                      onValueChange={(value) =>
                        handleFieldChange("category", value)
                      }
                      disabled={isLoadingCategories}
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={
                            isLoadingCategories
                              ? "Loading categories..."
                              : "Select category"
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {availableCategories.length > 0 ? (
                          availableCategories.map((cat) => (
                            <SelectItem key={cat} value={cat}>
                              {cat}
                            </SelectItem>
                          ))
                        ) : (
                          <div className="px-3 py-2 text-sm text-gray-500">
                            No categories available
                          </div>
                        )}
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-base text-gray-900">
                      {selectedMerchant._raw.category || "N/A"}
                    </p>
                  )}
                </div>

                <div className="space-y-2 md:col-span-2">
                  <label className="text-sm font-bold text-gray-700">
                    Address
                  </label>
                  {isEditMode ? (
                    <Textarea
                      value={editFormData.address}
                      onChange={(e) =>
                        handleFieldChange("address", e.target.value)
                      }
                      placeholder="Enter address"
                      rows={2}
                    />
                  ) : (
                    <p className="text-base text-gray-900">
                      {selectedMerchant._raw.address || "N/A"}
                    </p>
                  )}
                </div>

                <div className="space-y-2 md:col-span-2">
                  <label className="text-sm font-bold text-gray-700">
                    Website
                  </label>
                  {isEditMode ? (
                    <Input
                      value={editFormData.webSite}
                      onChange={(e) =>
                        handleFieldChange("webSite", e.target.value)
                      }
                      placeholder="Enter website URL"
                    />
                  ) : (
                    <p className="text-base text-gray-900 break-all">
                      {selectedMerchant._raw.webSite || "N/A"}
                    </p>
                  )}
                </div>

                {!isEditMode && (
                  <>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-gray-700">
                        Created By
                      </label>
                      <p className="text-base text-gray-900">
                        {selectedMerchant._raw.createdByName || "N/A"}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-bold text-gray-700">
                        Updated By
                      </label>
                      <p className="text-base text-gray-900">
                        {selectedMerchant._raw.updatedByName || "N/A"}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-bold text-gray-700">
                        Created Date
                      </label>
                      <p className="text-base text-gray-900">
                        {formatDate(selectedMerchant._raw.createdDatetime)}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-bold text-gray-700">
                        Updated Date
                      </label>
                      <p className="text-base text-gray-900">
                        {formatDate(selectedMerchant._raw.updatedDatetime)}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-bold text-gray-700">
                        Status
                      </label>
                      <p className="text-base text-gray-900">
                        {selectedMerchant._raw.isActive ? "Active" : "Inactive"}
                      </p>
                    </div>
                  </>
                )}
              </div>

              {/* Action Buttons for Edit Mode */}
              {isEditMode && (
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={handleCancelEdit}
                    disabled={isSaving}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveMerchant}
                    disabled={isSaving}
                    className="bg-amber-600 hover:bg-amber-700"
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GiftCardBrands;
